/**
 * The words a report prints about itself, in English (PDFs are English,
 * see `styles.ts`), in the viewer's own time zone. Dates are DD/MM/YYYY like
 * every GMS date; the weekday is SUKMA's "THU" prefix.
 */

const pad = (n: number) => String(n).padStart(2, "0");
const weekday = (d: Date) => d.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();

/** "As of MON 02/11/2026 14:05" — when the rows were read. */
export function asOfLabel(d: Date): string {
	return `As of ${weekday(d)} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Report created MON 02/11/2026 14:05" — the footer. */
export function generatedLabel(d: Date): string {
	return `Report created ${weekday(d)} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "15/11/2026" for a calendar date (`YYYY-MM-DD`) or a timestamp. */
export function reportDate(value: string | null | undefined): string {
	if (!value) return "-";
	const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? "-" : `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** "15/11/2026 14:05" in the viewer's zone. */
export function reportDateTime(value: string | null | undefined): string {
	if (!value) return "-";
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? "-" : `${reportDate(value)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Filters: Organisation THA · Status Approved" — printed so an export explains itself. */
export function filterLine(parts: readonly (readonly [string, string | null | undefined])[]): string | null {
	const set = parts.filter(([, v]) => v);
	return set.length ? `Filters: ${set.map(([k, v]) => `${k} ${v}`).join(" · ")}` : null;
}

/** A file name safe on every OS: `SEAG27_ACC_EntryList_20261102-1405.pdf`. */
export function reportFileName(code: string, name: string, d: Date): string {
	const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
	return `SEAG27_${code}_${name.replace(/[^A-Za-z0-9]+/g, "")}_${stamp}.pdf`;
}
